import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Leaflet CSS (injected once) ────────────────────────────────
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS  = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

function loadLeaflet() {
    return new Promise((resolve) => {
        if (window.L) { resolve(window.L); return; }

        // CSS
        if (!document.querySelector('link[href*="leaflet"]')) {
            const link = document.createElement('link');
            link.rel  = 'stylesheet';
            link.href = LEAFLET_CSS;
            document.head.appendChild(link);
        }

        // JS
        const script = document.createElement('script');
        script.src = LEAFLET_JS;
        script.onload = () => resolve(window.L);
        document.head.appendChild(script);
    });
}

const STYLES = `
.gm-root { display:flex; flex-direction:column; gap:16px; }
.gm-header { display:flex; justify-content:space-between; align-items:center; }
.gm-title { font-size:20px; font-weight:800; color:#0f172a; margin:0; }
.gm-sub { font-size:12px; color:#64748b; margin:3px 0 0; }
.gm-layout { display:grid; grid-template-columns:340px 1fr; gap:16px; }
.gm-sidebar { display:flex; flex-direction:column; gap:12px; }
.gm-card { background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:16px; }
.gm-card h3 { margin:0 0 12px; font-size:13px; font-weight:700; color:#0f172a; }
.gm-site-item { display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all 0.15s; margin-bottom:7px; }
.gm-site-item:hover { border-color:#3b82f6; background:#eff6ff; }
.gm-site-item.active { border-color:#1d4ed8; background:#eff6ff; }
.gm-site-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.gm-site-name { font-size:13px; font-weight:700; color:#0f172a; flex:1; }
.gm-site-status { font-size:10px; }
.gm-map-wrap { border-radius:12px; overflow:hidden; border:1px solid #e2e8f0; position:relative; }
.gm-mapbox { height:520px; width:100%; display:block; }
.gm-map-tip { position:absolute; top:12px; left:50%; transform:translateX(-50%); background:rgba(15,23,42,0.85); color:#fff; padding:8px 16px; border-radius:20px; font-size:12px; font-weight:600; z-index:1000; pointer-events:none; white-space:nowrap; }
.gm-form { display:flex; flex-direction:column; gap:10px; }
.gm-fg label { display:block; font-size:10px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:4px; }
.gm-fg input, .gm-fg select { width:100%; padding:8px 10px; border:1.5px solid #e2e8f0; border-radius:7px; font-size:13px; outline:none; font-family:inherit; color:#0f172a; }
.gm-fg input:focus, .gm-fg select:focus { border-color:#3b82f6; box-shadow:0 0 0 3px #eff6ff; }
.gm-coord { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:10px 12px; font-size:12px; color:#475569; }
.gm-coord span { font-weight:800; color:#1d4ed8; }
.gm-btn { padding:10px 16px; border-radius:8px; font-size:13px; font-weight:700; cursor:pointer; border:none; font-family:inherit; display:flex; align-items:center; gap:6px; justify-content:center; }
.gm-btn-primary { background:#1d4ed8; color:#fff; width:100%; }
.gm-btn-primary:hover { background:#1e40af; }
.gm-btn-danger { background:#fee2e2; color:#dc2626; }
.gm-btn-danger:hover { background:#fecaca; }
.gm-btn-sm { padding:5px 10px; font-size:11px; border-radius:6px; }
.gm-table { width:100%; border-collapse:collapse; }
.gm-table th { text-align:left; padding:8px 10px; font-size:10px; font-weight:700; color:#64748b; border-bottom:1px solid #e2e8f0; text-transform:uppercase; letter-spacing:0.4px; background:#f8fafc; }
.gm-table td { padding:11px 10px; border-bottom:1px solid #f8fafc; font-size:13px; color:#334155; }
.gm-table tr:last-child td { border-bottom:none; }
.gm-badge { padding:3px 8px; border-radius:4px; font-size:10px; font-weight:700; text-transform:uppercase; }
.gm-badge-set { background:#dcfce7; color:#166534; }
.gm-badge-unset { background:#fef3c7; color:#92400e; }
.gm-empty { text-align:center; padding:40px; color:#94a3b8; font-size:13px; }
.gm-search { display:flex; gap:8px; margin-bottom:0; }
.gm-search input { flex:1; padding:10px 12px 10px 36px; border:1.5px solid #e2e8f0; border-radius:9px; font-size:13px; outline:none; font-family:inherit; }
.gm-search input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px #eff6ff; }
.gm-loading { display:flex; align-items:center; justify-content:center; padding:60px; color:#64748b; font-size:13px; gap:8px; }
`;

const API_BASE = 'https://api.themrconstruction.com';

export default function GeofenceManager({ projects, geofences: gfProp, setGeofences, showToast, api }) {
    const mapRef       = useRef(null);
    const leafletRef   = useRef(null);
    const markersRef   = useRef({});
    const circlesRef   = useRef({});
    const tempMarkerRef = useRef(null);
    const mapDivRef    = useRef(null);

    const [loaded,      setLoaded]      = useState(false);
    const [geofences,   setLocal]       = useState([]);
    const [selected,    setSelected]    = useState(null);
    const [pinned,      setPinned]      = useState(null);
    const [radius,      setRadius]      = useState(150);
    const [saving,      setSaving]      = useState(false);
    const [mapMode,     setMapMode]     = useState('view');
    const [searchQ,     setSearchQ]     = useState('');
    const [searching,   setSearching]   = useState(false);
    const [searchErr,   setSearchErr]   = useState('');

    // Load geofences from server
    const loadGeofences = useCallback(async () => {
        try {
            const data = await api('geofence');
            const list = Array.isArray(data) ? data : [];
            setLocal(list);
            setGeofences(list);
        } catch { /* no geofences yet */ }
    }, [api, setGeofences]);

    // Search location using Nominatim (OpenStreetMap geocoder — free, no key)
    const searchLocation = useCallback(async () => {
        if (!searchQ.trim()) return;
        setSearching(true);
        setSearchErr('');
        try {
            const res  = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQ)}&format=json&limit=1&countrycodes=in`,
                { headers: { 'Accept-Language': 'en', 'User-Agent': 'MRConstructions/1.0' } }
            );
            const data = await res.json();
            if (!data || data.length === 0) {
                setSearchErr('Location not found. Try a more specific address.');
                setSearching(false);
                return;
            }
            const { lat, lon, display_name } = data[0];
            const L   = leafletRef.current;
            const map = mapRef.current;
            if (!map || !L) return;

            // Fly to location
            map.flyTo([parseFloat(lat), parseFloat(lon)], 16, { duration: 1.5 });

            // Drop a temporary search marker
            if (tempMarkerRef.searchMarker) {
                map.removeLayer(tempMarkerRef.searchMarker);
            }
            const searchIcon = L.divIcon({
                className: '',
                html: `<div style="background:#6366f1;width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 2px 8px rgba(99,102,241,0.5);"></div>`,
                iconSize: [22, 22],
                iconAnchor: [11, 22],
            });
            const sm = L.marker([parseFloat(lat), parseFloat(lon)], { icon: searchIcon })
                .addTo(map)
                .bindPopup(`<b>📍 ${display_name.split(',').slice(0,2).join(',')}</b><br><small>Click map to pin here</small>`)
                .openPopup();
            tempMarkerRef.searchMarker = sm;

            // Auto-set as pinned location
            setPinned({ lat: parseFloat(lat).toFixed(6), lng: parseFloat(lon).toFixed(6) });
            setMapMode('pin');

        } catch {
            setSearchErr('Search failed. Check your internet connection.');
        }
        setSearching(false);
    }, [searchQ]);

    // Load Leaflet + init map
    useEffect(() => {
        let cancelled = false;
        loadLeaflet().then(L => {
            if (cancelled) return;
            leafletRef.current = L;

            // Wait for DOM to be fully committed before initializing map
            const initMap = () => {
                if (cancelled || !mapDivRef.current || mapRef.current) return;

                delete L.Icon.Default.prototype._getIconUrl;
                L.Icon.Default.mergeOptions({
                    iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
                    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
                    shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
                });

                const map = L.map(mapDivRef.current, {
                    center: [20.5937, 78.9629],
                    zoom: 5,
                    zoomControl: true,
                    preferCanvas: true,
                });

                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                    maxZoom: 19,
                }).addTo(map);

                mapRef.current = map;
                // Force resize after render
                setTimeout(() => { map.invalidateSize(); }, 100);
                setLoaded(true);
                loadGeofences();
            };

            // Small delay ensures React has committed the DOM
            setTimeout(initMap, 50);
        });
        return () => {
            cancelled = true;
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [loadGeofences]);

    // Draw existing geofence markers/circles on map
    useEffect(() => {
        const map = mapRef.current;
        const L   = leafletRef.current;
        if (!map || !L || !loaded) return;

        // Clear old markers
        Object.values(markersRef.current).forEach(m => map.removeLayer(m));
        Object.values(circlesRef.current).forEach(c => map.removeLayer(c));
        markersRef.current = {};
        circlesRef.current = {};

        geofences.forEach(gf => {
            if (!gf.lat || !gf.lng) return;
            const lat = parseFloat(gf.lat);
            const lng = parseFloat(gf.lng);
            const rad = parseInt(gf.radius) || 150;

            // Circle
            const circle = L.circle([lat, lng], {
                radius: rad,
                color: '#1d4ed8',
                fillColor: '#3b82f6',
                fillOpacity: 0.15,
                weight: 2,
            }).addTo(map);
            circle.bindPopup(`<b>${gf.site}</b><br>Radius: ${rad}m`);
            circlesRef.current[gf.site] = circle;

            // Marker
            const marker = L.marker([lat, lng]).addTo(map);
            marker.bindTooltip(gf.site, { permanent: false, direction: 'top' });
            markersRef.current[gf.site] = marker;
        });

    }, [geofences, loaded]);

    // Map click handler for pinning
    useEffect(() => {
        const map = mapRef.current;
        const L   = leafletRef.current;
        if (!map || !L) return;

        const handler = (e) => {
            if (mapMode !== 'pin') return;
            const { lat, lng } = e.latlng;
            setPinned({ lat: lat.toFixed(6), lng: lng.toFixed(6) });

            // Show temp marker
            if (tempMarkerRef.current) map.removeLayer(tempMarkerRef.current);
            const redIcon = L.divIcon({
                className: '',
                html: `<div style="width:20px;height:20px;border-radius:50%;background:#ef4444;border:3px solid #fff;box-shadow:0 2px 8px rgba(239,68,68,0.5);"></div>`,
                iconSize: [20, 20],
                iconAnchor: [10, 10],
            });
            const m = L.marker([lat, lng], { icon: redIcon }).addTo(map);
            m.bindPopup(`<b>📍 Drop point</b><br>${lat.toFixed(5)}, ${lng.toFixed(5)}<br><small>Confirm in panel →</small>`).openPopup();
            tempMarkerRef.current = m;

            // Also draw preview circle
            if (tempMarkerRef.circleRef) map.removeLayer(tempMarkerRef.circleRef);
            const c = L.circle([lat, lng], {
                radius, color: '#ef4444', fillColor: '#fca5a5', fillOpacity: 0.2, weight: 2, dashArray: '5 5',
            }).addTo(map);
            tempMarkerRef.circleRef = c;
        };

        map.on('click', handler);
        return () => map.off('click', handler);
    }, [mapMode, radius]);

    // Focus map on selected project's existing geofence or India center
    const focusSite = (proj) => {
        setSelected(proj);
        setMapMode('view');
        setPinned(null);
        if (tempMarkerRef.current) {
            mapRef.current?.removeLayer(tempMarkerRef.current);
            if (tempMarkerRef.circleRef) mapRef.current?.removeLayer(tempMarkerRef.circleRef);
            tempMarkerRef.current = null;
        }

        const gf = geofences.find(g => g.site === proj.name);
        if (gf && mapRef.current) {
            mapRef.current.setView([parseFloat(gf.lat), parseFloat(gf.lng)], 16);
        }
    };

    const saveGeofence = async () => {
        if (!selected)          { showToast('Select a project first.'); return; }
        if (!pinned)            { showToast('Click on the map to drop a pin first.'); return; }
        if (!radius || radius < 50) { showToast('Minimum radius is 50m.'); return; }

        setSaving(true);
        try {
            await api('geofence', 'POST', {
                site:   selected.name,
                lat:    parseFloat(pinned.lat),
                lng:    parseFloat(pinned.lng),
                radius: parseInt(radius),
            });
            showToast(`✓ Geofence saved for ${selected.name} — engineers must be within ${radius}m to check in.`);
            await loadGeofences();
            setMapMode('view');
            setPinned(null);
            if (tempMarkerRef.current) {
                mapRef.current?.removeLayer(tempMarkerRef.current);
                if (tempMarkerRef.circleRef) mapRef.current?.removeLayer(tempMarkerRef.circleRef);
                tempMarkerRef.current = null;
            }
            // Pan to the new geofence
            if (mapRef.current) {
                mapRef.current.setView([parseFloat(pinned.lat), parseFloat(pinned.lng)], 16);
            }
        } catch (err) {
            showToast('Failed to save: ' + (err.message || 'Unknown error'));
        }
        setSaving(false);
    };

    const deleteGeofence = async (site) => {
        if (!window.confirm(`Remove geofence for ${site}? Engineers will not be able to check in.`)) return;
        try {
            const gf = geofences.find(g => g.site === site);
            if (gf?.id) await api(`geofence/${gf.id}`, 'DELETE');
            showToast(`Geofence removed for ${site}.`);
            await loadGeofences();
        } catch { showToast('Error removing geofence.'); }
    };

    const geoFor = (name) => geofences.find(g => g.site === name);

    return (
        <>
        <style>{STYLES}</style>
        <div className="gm-root">
            <div className="gm-header">
                <div>
                    <h2 className="gm-title">Site Geofence Manager</h2>
                    <p className="gm-sub">Pin each site on the map — engineers must be within the radius to mark attendance</p>
                </div>
                <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                    <span style={{fontSize:'12px',color:'#64748b',background:'#f1f5f9',padding:'5px 12px',borderRadius:'6px',border:'1px solid #e2e8f0'}}>
                        {geofences.length}/{projects.length} sites configured
                    </span>
                </div>
            </div>

            <div className="gm-layout">
                {/* ── LEFT PANEL ── */}
                <div className="gm-sidebar">

                    {/* Site list */}
                    <div className="gm-card">
                        <h3>📍 Sites</h3>
                        {projects.length === 0 && <div className="gm-empty">No projects yet.</div>}
                        {projects.map(p => {
                            const gf = geoFor(p.name);
                            return (
                                <div key={p.id}
                                    className={`gm-site-item ${selected?.id === p.id ? 'active' : ''}`}
                                    onClick={() => focusSite(p)}>
                                    <div className="gm-site-dot" style={{background: gf ? '#10b981' : '#f59e0b'}}/>
                                    <span className="gm-site-name">{p.name}</span>
                                    <span className="gm-badge" style={{background: gf ? '#dcfce7' : '#fef3c7', color: gf ? '#166534' : '#92400e'}}>
                                        {gf ? '✓ Set' : 'Not set'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pin controls */}
                    {selected && (
                        <div className="gm-card">
                            <h3>⚙ Configure: {selected.name}</h3>
                            <div className="gm-form">

                                {/* Mode toggle */}
                                <div style={{display:'flex',gap:'6px',marginBottom:'4px'}}>
                                    <button
                                        className={`gm-btn gm-btn-sm`}
                                        style={{flex:1, background: mapMode==='pin'?'#1d4ed8':'#f1f5f9', color: mapMode==='pin'?'#fff':'#374151', border:'1.5px solid '+(mapMode==='pin'?'#1d4ed8':'#e2e8f0')}}
                                        onClick={() => setMapMode(mapMode==='pin'?'view':'pin')}>
                                        {mapMode === 'pin' ? '🔴 Click map to drop pin' : '📍 Drop Pin on Map'}
                                    </button>
                                </div>
                                {mapMode === 'pin' && (
                                    <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'7px',padding:'8px 10px',fontSize:'11px',color:'#92400e',fontWeight:'600'}}>
                                        💡 Click anywhere on the map to drop the geofence pin for <strong>{selected.name}</strong>
                                    </div>
                                )}

                                {/* Show existing */}
                                {geoFor(selected.name) && !pinned && (
                                    <div className="gm-coord">
                                        Current pin: <span>{parseFloat(geoFor(selected.name).lat).toFixed(5)}, {parseFloat(geoFor(selected.name).lng).toFixed(5)}</span><br/>
                                        Radius: <span>{geoFor(selected.name).radius}m</span>
                                    </div>
                                )}

                                {/* New pin coords */}
                                {pinned && (
                                    <div className="gm-coord" style={{background:'#f0fdf4',border:'1px solid #bbf7d0'}}>
                                        📍 New pin: <span style={{color:'#16a34a'}}>{pinned.lat}, {pinned.lng}</span>
                                    </div>
                                )}

                                <div className="gm-fg">
                                    <label>Attendance Radius (metres)</label>
                                    <input type="number" min="50" max="2000" value={radius}
                                        onChange={e => setRadius(parseInt(e.target.value)||150)}
                                        placeholder="150"/>
                                    <div style={{fontSize:'10px',color:'#64748b',marginTop:'3px'}}>
                                        Engineers must be within {radius}m of the pin to check in.
                                        Typical: 100–300m for small site, 500m+ for large site.
                                    </div>
                                </div>

                                {/* Manual lat/lng input */}
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                                    <div className="gm-fg">
                                        <label>Latitude</label>
                                        <input type="number" step="0.00001" value={pinned?.lat || geoFor(selected.name)?.lat || ''}
                                            onChange={e => setPinned(p => ({...p, lat: e.target.value, lng: p?.lng || geoFor(selected.name)?.lng || ''}))}
                                            placeholder="e.g. 13.0827"/>
                                    </div>
                                    <div className="gm-fg">
                                        <label>Longitude</label>
                                        <input type="number" step="0.00001" value={pinned?.lng || geoFor(selected.name)?.lng || ''}
                                            onChange={e => setPinned(p => ({...p, lng: e.target.value, lat: p?.lat || geoFor(selected.name)?.lat || ''}))}
                                            placeholder="e.g. 80.2707"/>
                                    </div>
                                </div>

                                <button className="gm-btn gm-btn-primary" onClick={saveGeofence} disabled={saving}>
                                    {saving ? 'Saving…' : '✓ Save Geofence'}
                                </button>

                                {geoFor(selected.name) && (
                                    <button className="gm-btn gm-btn-danger gm-btn-sm" onClick={() => deleteGeofence(selected.name)}>
                                        🗑 Remove Geofence
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {!selected && (
                        <div className="gm-card" style={{textAlign:'center',padding:'24px',color:'#94a3b8'}}>
                            <div style={{fontSize:'32px',marginBottom:'8px'}}>🗺</div>
                            <div style={{fontWeight:'600',fontSize:'13px'}}>Select a site from the list above</div>
                            <div style={{fontSize:'11px',marginTop:'4px'}}>Then click on the map to set its attendance boundary</div>
                        </div>
                    )}
                </div>

                {/* ── MAP ── */}
                <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>

                    {/* Search bar */}
                    <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                        <div style={{flex:1,position:'relative'}}>
                            <span style={{position:'absolute',left:'11px',top:'50%',transform:'translateY(-50%)',fontSize:'16px',pointerEvents:'none'}}>🔍</span>
                            <input
                                value={searchQ}
                                onChange={e => setSearchQ(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && searchLocation()}
                                placeholder="Search location… e.g. Anna Nagar Chennai, MG Road Bangalore"
                                style={{width:'100%',padding:'10px 12px 10px 36px',border:'1.5px solid #e2e8f0',borderRadius:'9px',fontSize:'13px',outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
                                onFocus={e=>e.target.style.borderColor='#3b82f6'}
                                onBlur={e=>e.target.style.borderColor='#e2e8f0'}
                            />
                        </div>
                        <button
                            onClick={searchLocation}
                            disabled={searching || !searchQ.trim()}
                            style={{padding:'10px 18px',background:searching?'#94a3b8':'#1d4ed8',color:'#fff',border:'none',borderRadius:'9px',fontWeight:'700',fontSize:'13px',cursor:'pointer',whiteSpace:'nowrap',fontFamily:'inherit',flexShrink:0}}>
                            {searching ? '⏳ Searching…' : '🔍 Search'}
                        </button>
                    </div>

                    {searchErr && (
                        <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:'7px',padding:'8px 12px',fontSize:'12px',color:'#dc2626',fontWeight:'600'}}>
                            ⚠ {searchErr}
                        </div>
                    )}

                    {pinned && !mapMode !== 'view' && (
                        <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'7px',padding:'8px 12px',fontSize:'12px',color:'#166534',fontWeight:'600',display:'flex',alignItems:'center',gap:'6px'}}>
                            ✓ Location found — now fine-tune by clicking on the map, then click <strong>Save Geofence</strong>
                        </div>
                    )}

                <div className="gm-map-wrap">
                    {mapMode === 'pin' && (
                        <div className="gm-map-tip">
                            📍 Click anywhere to place the pin for <strong>{selected?.name}</strong>
                        </div>
                    )}
                    <div ref={mapDivRef}
                        style={{
                            cursor: mapMode === 'pin' ? 'crosshair' : 'grab',
                            height: '520px',
                            width: '100%',
                            display: 'block',
                            position: 'relative',
                            zIndex: 1,
                        }}/>
                    {!loaded && (
                        <div className="gm-loading" style={{position:'absolute',inset:0,background:'#f8fafc',zIndex:10}}>
                            Loading map…
                        </div>
                    )}
                </div>
                </div>
            </div>

            {/* ── SUMMARY TABLE ── */}
            <div className="gm-card">
                <h3 style={{marginBottom:'14px'}}>All Site Geofences</h3>
                <table className="gm-table">
                    <thead><tr><th>Site</th><th>Latitude</th><th>Longitude</th><th>Radius</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                        {projects.map(p => {
                            const gf = geoFor(p.name);
                            return (
                                <tr key={p.id}>
                                    <td style={{fontWeight:'700'}}>{p.name}</td>
                                    <td style={{fontFamily:'monospace',fontSize:'12px',color:'#475569'}}>{gf ? parseFloat(gf.lat).toFixed(5) : '—'}</td>
                                    <td style={{fontFamily:'monospace',fontSize:'12px',color:'#475569'}}>{gf ? parseFloat(gf.lng).toFixed(5) : '—'}</td>
                                    <td>{gf ? `${gf.radius}m` : '—'}</td>
                                    <td>
                                        <span className={`gm-badge ${gf ? 'gm-badge-set' : 'gm-badge-unset'}`}>
                                            {gf ? '✓ Configured' : '⚠ Not set'}
                                        </span>
                                    </td>
                                    <td style={{display:'flex',gap:'6px'}}>
                                        <button className="gm-btn gm-btn-sm"
                                            style={{background:'#eff6ff',color:'#1d4ed8',border:'1px solid #bfdbfe'}}
                                            onClick={() => focusSite(p)}>
                                            {gf ? 'Edit' : 'Set Pin'}
                                        </button>
                                        {gf && (
                                            <button className="gm-btn gm-btn-sm gm-btn-danger"
                                                onClick={() => deleteGeofence(p.name)}>
                                                Remove
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {projects.length === 0 && (
                            <tr><td colSpan="6" className="gm-empty">No projects found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
        </>
    );
}
